/* ============================================================
 * N2T ship-week zone map — ZIP-5 lookup (FedEx from Americold 62071)
 *
 * Maps every US ZIP-5 (40,814 entries) to its FedEx Home Delivery
 * business-days-in-transit count from the Americold warehouse
 * (62071 / National Stock Yards, IL). Pulled 2026-08-20 from the
 * FedEx Rates & Transit Times API, service GROUND_HOME_DELIVERY
 * with residential: true — which is how N2T actually ships.
 *
 * Source data + harvester live in the monorepo
 * (paccloud/nettotableseafoods) at `shipping/fedex-transit-62071/`:
 * `fedex_transit_pull.py` is resumable and re-runs in ~45 min.
 * Rebuild when the origin or the FedEx service level changes.
 *
 * Superseded the CC3PL/UPS map (60090 / Wheeling IL, 40,992 ZIPs
 * from `shipping/2024 60090_Outbound.xlsx`) — see #199.
 *
 * 178 ZIPs present in the old map are absent here: 176 Puerto Rico
 * (FedEx Home Delivery does not serve PR) plus two retired ZIPs
 * (74729, 95915). Those fall through to lookup() returning null.
 *
 * Separately, the map DOES carry Alaska (273 ZIPs) and Hawaii (136),
 * because the carrier quotes them — but the store's Shopify delivery
 * zone is the 48 contiguous states + DC, so those are filtered out by
 * `unservedRegion()` before any date is quoted. See the
 * `unserved_zip_ranges` note in ship-week-warehouse.js.
 *
 * The data file (`assets/ship-week-zones-data.json`, ~400KB raw /
 * ~80KB gzipped) is fetched LAZILY on first lookup — never inlined
 * on the PDP — so first-paint page weight is unchanged.
 *
 * `lookup()` returns a zone with effective `transit_days` after
 * applying N2T's air-upgrade policy from window.N2T_SHIP_WAREHOUSE:
 * anything > 3 ground days ships 2nd Day Air (2 days) instead, to
 * protect cold-chain on perishable seafood.
 *
 * US-only: N2T does not ship to Canada/Mexico/international.
 * ZIPs outside the US 5-digit format return null and the pill
 * renders the "ZIP not recognized" state.
 *
 * Async surface = `loadZones()` only. Callers should:
 *   await window.N2T_SHIP_ZONES.loadZones();
 *   const zone = window.N2T_SHIP_ZONES.lookup(zip);
 *
 * `lookup()` is intentionally synchronous: callers (PDP pill,
 * cart-stage component, tests) keep their existing sync contracts.
 * It returns null until loadZones() resolves; consumers re-render
 * after the load completes.
 * ============================================================ */
(function () {
  'use strict';

  // Idempotency guard. theme.liquid loads this file globally (for the
  // cart-stage component) AND snippets/ship-week-display.liquid loads it
  // for the PDP pill. Two <script> tags with the same URL = two IIFE
  // executions, which would wipe the _data cache between them. Skip the
  // second one — window.N2T_SHIP_ZONES is already wired up.
  if (typeof window !== 'undefined' && window.N2T_SHIP_ZONES) return;

  // Module-private cache. `_data` is null until loadZones() resolves;
  // `_loadPromise` is the in-flight fetch (idempotent across callers).
  let _data = null;
  let _loadPromise = null;

  function loadZones() {
    if (_data) return Promise.resolve(_data);
    if (_loadPromise) return _loadPromise;

    const url = (typeof window !== 'undefined') ? window.N2T_SHIP_ZONES_URL : null;
    if (!url) {
      _data = {};
      return Promise.resolve(_data);
    }

    _loadPromise = fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (d) { _data = (d && typeof d === 'object') ? d : {}; return _data; })
      .catch(function () { _data = {}; return _data; });

    return _loadPromise;
  }

  // Returns the human label for a destination the store does not ship to
  // ("Alaska", "Hawaii", …), or null when the ZIP is inside the shipping
  // footprint. Policy comes from window.N2T_SHIP_WAREHOUSE.unserved_zip_ranges,
  // which mirrors the Shopify Domestic delivery zone — see that file.
  //
  // Separate from lookup() on purpose. lookup() must keep returning null for
  // these ZIPs so the cart-stage consumer classifies them as `unknown_zone`
  // and writes no ship-week cart attributes; only the PDP pill needs the
  // label, and it needs it to say something better than "not recognized".
  function unservedRegion(zip) {
    if (!isValidUSZip(zip)) return null;
    const n = parseInt(zip.trim().slice(0, 5), 10);
    if (!Number.isFinite(n)) return null;

    const cfg = (typeof window !== 'undefined' && window.N2T_SHIP_WAREHOUSE) || {};
    const ranges = cfg.unserved_zip_ranges || [];
    for (let i = 0; i < ranges.length; i++) {
      const r = ranges[i];
      if (n >= r[0] && n <= r[1]) return r[2] || 'that area';
    }
    return null;
  }

  function lookup(zip) {
    if (typeof zip !== 'string') return null;
    if (!isValidUSZip(zip)) return null;
    // Destinations outside the store's shipping footprint never get a
    // transit answer, even though the carrier map has one for them.
    if (unservedRegion(zip)) return null;
    if (!_data) return null;

    const groundDays = _data[zip.trim().slice(0, 5)];
    if (typeof groundDays !== 'number') return null;

    // N2T's air-upgrade policy — read at call time so warehouse-config
    // changes propagate without reload. Behavior preserved from v1 stub.
    const cfg = (typeof window !== 'undefined' && window.N2T_SHIP_WAREHOUSE) || {};
    const threshold = (typeof cfg.air_upgrade_threshold_days === 'number')
      ? cfg.air_upgrade_threshold_days
      : Infinity;
    const airDays = (typeof cfg.air_transit_days === 'number')
      ? cfg.air_transit_days
      : 2;
    const upgradeToAir = groundDays > threshold;

    return {
      name: upgradeToAir ? ('Air-' + airDays + 'd') : ('Ground-' + groundDays + 'd'),
      ground_transit_days: groundDays,
      transit_days: upgradeToAir ? airDays : groundDays,
      service: upgradeToAir ? '2nd Day Air' : 'Ground',
    };
  }

  // True once loadZones() has resolved with a non-empty map. Lets callers
  // tell "the transit map hasn't arrived yet (or failed)" apart from "this
  // ZIP isn't in the map" — lookup() returns null for both, but only the
  // second is a real not-served answer worth reporting.
  function hasData() {
    return _data !== null && Object.keys(_data).length > 0;
  }

  function isValidUSZip(input) {
    return typeof input === 'string' && /^\d{5}(-\d{4})?$/.test(input.trim());
  }

  // Test-only: lets the vitest suite seed the cache with a small fixture
  // without going through fetch(). No-op in production (callers should
  // never invoke this from theme code).
  function _setDataForTests(data) {
    _data = (data && typeof data === 'object') ? data : null;
    _loadPromise = null;
  }

  if (typeof window !== 'undefined') {
    window.N2T_SHIP_ZONES = {
      lookup: lookup,
      isValidUSZip: isValidUSZip,
      loadZones: loadZones,
      hasData: hasData,
      unservedRegion: unservedRegion,
      _setDataForTests: _setDataForTests,
    };
  }
})();
